import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";

import type { ICodeItem } from "web/types/Code";
import type { Nullable } from "web/types/Utils";

import { fetchCodes, setCodeUsed } from "./codesThunks";

interface ICodesCacheEntry {
  codes: ICodeItem[];
  toUseCount: number;
  usedCount: number;
  canceledCount: number;
  totalCount: number;
}

interface ICodesState {
  items: ICodeItem[];
  settingMarkIds: number[];
  itemsIdsMarkedAsUsed: number[];
  itemsIdsMarkedAsToUse: number[];
  isCodesGotten: boolean;
  error: Nullable<Error>;
  isLoading: boolean;
  toUseCount: number;
  usedCount: number;
  canceledCount: number;
  totalCount: number;
  cache: {
    [key: string]: ICodesCacheEntry;
  };
}

const initialState: ICodesState = {
  items: [],
  settingMarkIds: [],
  itemsIdsMarkedAsUsed: [],
  itemsIdsMarkedAsToUse: [],
  isCodesGotten: false,
  error: null,
  isLoading: false,
  toUseCount: 0,
  usedCount: 0,
  canceledCount: 0,
  totalCount: 0,
  cache: {},
};

export const codesSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    resetCodesCache: (state) => {
      state.cache = {};
    },
    hydrateCodesFromCache: (state, action: PayloadAction<ICodesCacheEntry>) => {
      const { payload } = action;
      return {
        ...state,
        items: payload.codes,
        itemsIdsMarkedAsUsed: initialState.itemsIdsMarkedAsUsed,
        itemsIdsMarkedAsToUse: initialState.itemsIdsMarkedAsToUse,
        isCodesGotten: true,
        toUseCount: payload.toUseCount,
        usedCount: payload.usedCount,
        canceledCount: payload.canceledCount,
        totalCount: payload.totalCount,
      };
    },
    setCodeUsedRequest: (state, action) => {
      const { payload } = action;
      return {
        ...state,
        settingMarkIds: payload.mark,
        itemsIdsMarkedAsUsed: payload.used,
        itemsIdsMarkedAsToUse: payload.toUse,
      };
    },
    revertCodeUsed: (state, action) => {
      const { payload } = action;
      return {
        ...state,
        settingMarkIds: payload.mark,
      };
    },
  },
  extraReducers: (builder) => {
    // fetch codes

    builder.addCase(fetchCodes.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchCodes.fulfilled, (state, action) => {
      const { payload } = action;
      const { cacheKey } = payload;

      return {
        ...state,
        items: payload.codes,
        itemsIdsMarkedAsUsed: initialState.itemsIdsMarkedAsUsed,
        itemsIdsMarkedAsToUse: initialState.itemsIdsMarkedAsToUse,
        isLoading: false,
        isCodesGotten: true,
        toUseCount: payload.toUseCount,
        usedCount: payload.usedCount,
        canceledCount: payload.canceledCount,
        totalCount: payload.totalCount,
        cache: {
          ...state.cache,
          [cacheKey]: {
            codes: payload.codes,
            toUseCount: payload.toUseCount,
            usedCount: payload.usedCount,
            canceledCount: payload.canceledCount,
            totalCount: payload.totalCount,
          },
        },
      };
    });

    builder.addCase(fetchCodes.rejected, (state, action) => {
      const { error } = action as { error: Error };
      newRelicErrorReport(error, "codesSlice - fetchCodes");

      return {
        ...state,
        error,
        isLoading: false,
        isCodesGotten: true,
      };
    });

    // set code used

    builder.addCase(setCodeUsed.fulfilled, (state, action) => {
      const { payload } = action;
      return {
        ...state,
        settingMarkIds: payload.mark,
        cache: {},
      };
    });

    builder.addCase(setCodeUsed.rejected, (state, action) => {
      const { error } = action;
      newRelicErrorReport(error, "codesSlice - setCodeUsed");
    });
  },
});

export const {
  setCodeUsedRequest,
  revertCodeUsed,
  resetCodesCache,
  hydrateCodesFromCache,
} = codesSlice.actions;

export default codesSlice.reducer;
